import * as Sentry from "@sentry/react";
import type { ContentI18n } from "@/lib/questContent/types";
import type { IQuestService, CreateQuestOptions, UpdateQuestOptions } from "@/services/interfaces/IQuestService";
import { QuestNotFoundError } from "@/lib/questErrors";
import type {
  Quest,
  Task,
  TaskTreeNode,
  CreateQuestInput,
  CreateTaskInput,
  QuestFilters,
  PaginatedResult,
  QuestTemplate,
} from "@/lib/types";
import { supabase } from "@/lib/supabase";
import { callEdgeFunction } from "@/lib/supabaseAuth";
import type { PolygonClient } from "@/lib/polygon";
import { getChainConfig } from "@/config/contracts";
import { getTokenConfigs } from "@/config/tokens";
import { isKnownCurrency } from "@/lib/types";
import type { KnownCurrency } from "@/lib/types";
import { assertOnActiveChain } from "@/lib/chainGuard";
import { UnstaffedTaskError } from "@/services/errors";
import { isMainnetChainId } from "@/config/activeChain";
import { isVolunteerQuest } from "@/lib/utils";
import { mapTaskTrustColumns, type TaskTrustRow } from "@/lib/trustGrade/mapRow";
import { sanitizeTrustFactors } from "@/lib/trustGrade/rubric";
import { TRUST_FACTOR_SOURCES, type TrustFactors, type TrustFactorsSource } from "@/lib/trustGrade/types";
import { TERMINAL_QUEST_STATUSES } from "@/lib/questStatus";

// ─── Helpers ────────────────────────────────────────────

/** Resolve a raw currency string to KnownCurrency, logging a warning on fallback. */
function resolveKnownCurrency(raw: string, context: string): KnownCurrency {
  if (isKnownCurrency(raw)) return raw;
  console.warn(`Unknown currency in ${context}: ${raw}, defaulting to USDT`);
  return 'USDT';
}

// ─── Row mappers ─────────────────────────────────────────

export interface QuestRow {
  id: string;
  creator_id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  total_budget_usd: number;
  currency: string;
  deadline: string | null;
  created_at: string;
  updated_at: string;
  chain_quest_id: number | null;
  chain_tx_hash: string | null;
  location_requirement: string | null;
  /** DEV-824: machine-written translations. `select("*")` already returns
   *  these; before this they were fetched and then dropped by the mapper. */
  i18n: ContentI18n | null;
  source_lang: string | null;
  template_id: string | null;
  is_template_anchor: boolean | null;
  organization_id?: string | null;
  tasks?: TaskRow[];
}

export interface QuestTemplateRow {
  id: string;
  creator_id: string;
  title: string;
  description: string;
  category: string;
  budget_per_quest: number;
  currency: string;
  deadline: string | null;
  task_definition: Record<string, unknown>;
  max_slots: number;
  filled_slots: number;
  status: string;
  anchor_quest_id: string | null;
  chain_id: number;
  created_at: string;
  updated_at: string;
}

interface TaskRow extends TaskTrustRow {
  id: string;
  quest_id: string;
  parent_task_id: string | null;
  title: string;
  description: string;
  task_type: string;
  status: string;
  budget_usd: number;
  estimated_effort_hours: number;
  deadline: string | null;
  skills: string[];
  assignee_id: string | null;
  order: number;
  created_at: string;
  updated_at: string;
  chain_task_id: number | null;
  chain_tx_hash: string | null;
  chain_assigned: boolean;
  work_mode: string | null;
  location_countries: string[] | null;
  location_cities: string[] | null;
  /** DEV-824: machine-written translations. `select("*")` already returned
   *  these; before this they were fetched and then dropped by the mapper, so
   *  the detail route had nothing to localize. */
  i18n: ContentI18n | null;
  source_lang: string | null;
}

export function mapQuestRow(row: QuestRow): Quest {
  const tasks = row.tasks ?? [];
  return {
    id: row.id,
    creatorId: row.creator_id,
    title: row.title,
    description: row.description,
    category: row.category,
    status: row.status as Quest["status"],
    totalBudgetUSD: Number(row.total_budget_usd),
    currency: resolveKnownCurrency(row.currency, `quest ${row.id}`),
    deadline: row.deadline,
    attachments: [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    taskCount: tasks.length,
    completedTaskCount: tasks.filter((t) => t.status === "completed").length,
    chainQuestId: row.chain_quest_id ?? null,
    chainTxHash: row.chain_tx_hash ?? null,
    locationRequirement: (row.location_requirement ?? "remote") as Quest["locationRequirement"],
    i18n: row.i18n ?? null,
    sourceLang: row.source_lang ?? null,
    templateId: row.template_id ?? null,
    isTemplateAnchor: row.is_template_anchor ?? false,
    organizationId: row.organization_id ?? null,
  };
}

export function mapTemplateRow(row: QuestTemplateRow): QuestTemplate {
  const td = row.task_definition as Record<string, unknown>;
  return {
    id: row.id,
    creatorId: row.creator_id,
    title: row.title,
    description: row.description,
    category: row.category,
    budgetPerQuest: Number(row.budget_per_quest),
    currency: resolveKnownCurrency(row.currency, `template ${row.id}`),
    deadline: row.deadline,
    taskDefinition: {
      title: (td.title as string) ?? '',
      description: td.description as string | undefined,
      taskType: td.taskType as QuestTemplate['taskDefinition']['taskType'],
      estimatedEffortHours: td.estimatedEffortHours as number | undefined,
      skills: td.skills as string[] | undefined,
      // What the inherit trigger copies into each hired clone (sanitized there too).
      trustFactors: sanitizeTrustFactors(td.trustFactors),
      trustFactorsSource: (TRUST_FACTOR_SOURCES as readonly unknown[]).includes(td.trustFactorsSource)
        ? (td.trustFactorsSource as TrustFactorsSource)
        : undefined,
    },
    maxSlots: row.max_slots,
    filledSlots: row.filled_slots,
    status: row.status as QuestTemplate['status'],
    anchorQuestId: row.anchor_quest_id,
    chainId: row.chain_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapTaskRow(row: TaskRow): Task {
  return {
    id: row.id,
    questId: row.quest_id,
    parentTaskId: row.parent_task_id,
    title: row.title,
    description: row.description,
    taskType: row.task_type as Task["taskType"],
    status: row.status as Task["status"],
    budgetUSD: Number(row.budget_usd),
    estimatedEffortHours: Number(row.estimated_effort_hours),
    deadline: row.deadline,
    skills: row.skills ?? [],
    assigneeId: row.assignee_id,
    order: row.order,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    chainTaskId: row.chain_task_id ?? null,
    chainTxHash: row.chain_tx_hash ?? null,
    chainAssigned: row.chain_assigned ?? false,
    workMode: (row.work_mode ?? "remote") as Task["workMode"],
    locationCountries: row.location_countries ?? [],
    locationCities: row.location_cities ?? [],
    i18n: row.i18n ?? null,
    sourceLang: row.source_lang ?? null,
    ...mapTaskTrustColumns(row),
  };
}

function buildTaskTree(tasks: Task[]): TaskTreeNode[] {
  const map = new Map<string, TaskTreeNode>();
  const roots: TaskTreeNode[] = [];

  for (const t of tasks) {
    map.set(t.id, { ...t, children: [] });
  }

  for (const t of tasks) {
    const node = map.get(t.id)!;
    if (t.parentTaskId && map.has(t.parentTaskId)) {
      map.get(t.parentTaskId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  roots.sort((a, b) => a.order - b.order);
  return roots;
}

// ─── Service ─────────────────────────────────────────────

export class SupabaseQuestService implements IQuestService {
  constructor(
    private polygonClient: PolygonClient | null = null,
  ) {}

  async createQuest(input: CreateQuestInput, options?: CreateQuestOptions): Promise<Quest> {
    const onStep = options?.onStepChange;

    let chainQuestId: number | undefined;
    let chainTxHash: string | undefined;
    const chainTaskIds: Record<number, number> = {}; // order -> chainTaskId

    // "Post as" is re-checked by the quests function and the DB trigger, but
    // only at the DB save — after the escrow below is funded. Refuse first so
    // a stale or foreign org never strands funds on-chain.
    if (input.organizationId) {
      const { data: member, error } = await supabase.rpc("is_org_member", { p_org: input.organizationId });
      if (error) throw new Error(`Failed to check organization membership: ${error.message}`);
      if (member !== true) throw new Error("org_not_member");
    }

    // ── Chain-first: create quest + fund + create tasks on-chain ──
    // Skip entirely for AI-only quests (no budget → no escrow needed).
    if (this.polygonClient && input.totalBudgetUSD > 0) {
      const cfg = getChainConfig();
      const deadlineUnix = input.deadline
        ? Math.floor(new Date(input.deadline).getTime() / 1000)
        : Math.floor(Date.now() / 1000) + 365 * 24 * 3600; // 1 year default

      // Resolve token config for the selected currency
      const currency = input.currency;
      const tokenConfig = getTokenConfigs().get(currency);
      if (!tokenConfig) throw new Error(`Unsupported currency: ${currency}`);

      // 0. Testnet preflight: ensure user has enough test tokens
      //    MockERC20.mint() is open to anyone; silently fund the wallet before approve.
      if (!isMainnetChainId(cfg.chainId)) {
        const userAddr = await this.polygonClient.getAddress();
        const balance = await this.polygonClient.getTokenBalance(currency, userAddr);
        if (balance < input.totalBudgetUSD) {
          await this.polygonClient.mintTestToken(
            currency,
            userAddr,
            input.totalBudgetUSD - balance,
          );
        }
      }

      // Per-tx 429 retries are handled by PolygonClient.executeSend's
      // 4-attempt exponential backoff. Sequential awaits already serialize
      // sponsored sends through the bundler's per-account nonce queue.

      // 1. Approve QuestManager to transferFrom user's tokens into escrow
      onStep?.("approve", "active");
      await this.polygonClient.approveToken(
        currency,
        cfg.contracts.questManager,
        input.totalBudgetUSD,
      );
      onStep?.("approve", "done");

      // 2. Create quest on-chain (with token address)
      onStep?.("create", "active");
      const { questId, receipt } = await this.polygonClient.createQuest(
        { title: input.title, description: input.description },
        input.category,
        input.totalBudgetUSD,
        deadlineUnix,
        tokenConfig.address,
      );
      chainQuestId = Number(questId);
      chainTxHash = receipt.hash;
      onStep?.("create", "done");

      // 3. Fund quest (moves USDC to escrow)
      onStep?.("fund", "active");
      await this.polygonClient.fundQuest(questId);
      onStep?.("fund", "done");

      // 4. Create tasks on-chain (zero-budget tasks are DB-only)
      onStep?.("tasks", "active");
      const flatTasks = flattenTasks(input.tasks);
      for (const t of flatTasks) {
        const budget = t.budgetUSD ?? 0;
        if (budget <= 0) continue; // TaskManager.createTask rejects zero-budget

        const taskDeadline = t.deadline
          ? Math.floor(new Date(t.deadline).getTime() / 1000)
          : deadlineUnix;

        const { taskId } = await this.polygonClient.createTask(
          questId,
          { title: t.title, description: t.description },
          t.taskType,
          budget,
          t.estimatedEffortHours,
          taskDeadline,
          t.skills,
          t.order ?? 1,
        );
        chainTaskIds[t.order ?? 1] = Number(taskId);
      }
      onStep?.("tasks", "done");

      // 5. Publish quest (Funded → Active) and open all tasks (Draft → Open)
      onStep?.("publish", "active");
      await this.polygonClient.publishQuest(BigInt(chainQuestId));
      await this.polygonClient.openAllTasks(BigInt(chainQuestId));
      onStep?.("publish", "done");
    }

    // ── DB-second: persist to Supabase via edge function ──
    onStep?.("db_save", "active");
    const payload: Record<string, unknown> = {
      title: input.title,
      description: input.description,
      category: input.category,
      totalBudgetUSD: input.totalBudgetUSD,
      currency: input.currency,
      deadline: input.deadline || null,
      tasks: mapCreateTaskInputs(input.tasks),
      chainQuestId,
      chainTxHash,
      chainTaskIds,
      chainId: getChainConfig().chainId,
      organizationId: input.organizationId ?? null,
    };

    const result = await callEdgeFunction<Quest>("quests", { body: payload });
    onStep?.("db_save", "done");
    return result;
  }

  async publishForNegotiation(
    input: CreateQuestInput,
    options?: CreateQuestOptions,
  ): Promise<Quest> {
    const onStep = options?.onStepChange;

    // No on-chain calls — save directly to Supabase with "published" status.
    // Tasks get "open" status so workers can discover and propose prices.
    onStep?.("db_save", "active");
    const payload: Record<string, unknown> = {
      title: input.title,
      description: input.description,
      category: input.category,
      totalBudgetUSD: input.totalBudgetUSD,
      currency: input.currency,
      deadline: input.deadline || null,
      tasks: mapCreateTaskInputs(input.tasks),
      status: "published",
      chainId: getChainConfig().chainId,
      organizationId: input.organizationId ?? null,
    };

    const result = await callEdgeFunction<Quest>("quests", { body: payload });
    onStep?.("db_save", "done");
    return result;
  }

  /**
   * Retire an unfilled task. The whole rule lives in the RPC
   * (20260917100000): creator-or-admin, not yet on-chain, no accepted
   * proposal, idempotent. Nothing is re-checked here — a second copy of the
   * rule in the client is a copy that can disagree with the one that counts,
   * and this one cannot see concurrent acceptances anyway.
   */
  async dropTask(taskId: string): Promise<void> {
    const { error } = await supabase.rpc("creator_drop_task", { p_task_id: taskId });
    if (error) throw new Error(`Failed to drop task: ${error.message}`);
  }

  async lockFundsAndStart(questId: string, options?: CreateQuestOptions): Promise<Quest> {
    const onStep = options?.onStepChange;

    // NOTE: no polygonClient guard here. A volunteer ($0) quest settles entirely
    // in the DB and must start without a wallet (DEV-246 R5); the paid branch
    // below carries its own guard before the first on-chain call.

    // 1. Read quest + accepted proposals + task count concurrently (independent reads).
    const [
      { data: questRow, error: qErr },
      { data: acceptedProposals, error: propErr },
      { data: taskIdRows, error: countErr },
    ] = await Promise.all([
      supabase.from("quests").select("*").eq("id", questId).single(),
      supabase
        .from("proposals")
        .select("*")
        .eq("quest_id", questId)
        .eq("status", "accepted"),
      supabase.from("tasks").select("id, budget_usd, status").eq("quest_id", questId),
    ]);
    if (qErr || !questRow) throw new Error(`Quest not found: ${qErr?.message}`);

    // ── Authoritative funding gate ──────────────────────────────────────────
    //
    // Deliberately checked HERE, above step 1b, and not after the re-read
    // further down. Step 1b assigns workers, supersedes rival proposals and
    // inserts workspace_members, and throwing undoes none of it — so guarding
    // below it meant every retry mutated the quest it was refusing to start,
    // and the superseded proposals were never restored. What this reads is
    // stable across step 1b, which only updates existing task rows.
    if (countErr) throw new Error(`Failed to count tasks: ${countErr.message}`);
    // The gate keys on this set, so an unread proposal list must not read as
    // "nobody is staffed". Unchecked, an RLS block or a transient error makes
    // acceptedProposals null, empties the set, and tells a creator whose quest
    // is fully staffed that every task is unstaffed.
    if (propErr) throw new Error(`Failed to read accepted proposals: ${propErr.message}`);
    if (!acceptedProposals) {
      throw new Error("Failed to read accepted proposals: no data returned (possible RLS block).");
    }
    // Supabase can return data=null with error=null on an RLS silent block.
    // Fail closed: an unreadable task list must not look fully staffed.
    if (!taskIdRows) {
      throw new Error("Failed to read tasks: no data returned (possible RLS block).");
    }

    // ProposalCard's `allTasksAgreed` is an affordance; this is the control.
    // It runs in the client on data the client fetched, and a second tab, a
    // retry or a direct service call reaches this method without it.
    //
    // It has to be a control because escrow is created ONCE with an immutable
    // total — EscrowVault.createEscrow reverts EscrowExists — so a task still
    // unstaffed at this instant can never be funded afterwards. The quest
    // would be live, funded, and permanently unable to pay that task.
    //
    // ── Why an accepted proposal, and NOT assignee_id ──────────────────────
    // `can_read_task_thread` (20260916100000) treats assignee_id as a second
    // entitlement source, because BountyBoard.acceptApplicant assigns without
    // writing a proposal row. This gate deliberately does NOT, because the
    // `tasks_claim_open` policy lets ANY authenticated user set assignee_id on
    // a task that is open and unassigned — and tasks are 'open' from publish,
    // which is BEFORE funding. Honouring assignee_id here would let a stranger
    // self-assign a task and unlock the creator's escrow at the posted price,
    // with no agreed price and no acceptance.
    //
    // The two failure modes are not symmetric, which is what settles it:
    // refusing to fund a legitimately self-claimed task is recoverable (accept
    // a proposal, retry), while funding an unwanted assignment is permanent.
    //
    // ── Only proposals on tasks that are still live ────────────────────────
    // A dropped task (creator_drop_task, status 'cancelled') keeps its row, and
    // an offer on it can still be — or already be — 'accepted'. Applying it in
    // step 1b would re-assign the dropped worker, flip the task back to
    // 'assigned' (the creator is exempt from the cancelled pin) and re-add them
    // to the workspace they were removed from (20260925140000). The DB refuses
    // new acceptances on a cancelled task; this is the client half.
    //
    // An allow-list over THIS quest's live tasks, not a deny-list of its
    // cancelled ones: proposals_insert_worker does not tie a proposal's task to
    // its quest, so a worker can plant an 'accepted' row carrying this quest's
    // id and a live task id from another quest. A deny-list would let it
    // through to the workspace_members upsert below.
    const liveTaskIds = new Set(
      taskIdRows
        .filter((t: { status: string }) => t.status !== "cancelled")
        .map((t: { id: string }) => t.id),
    );
    const liveAcceptedProposals = acceptedProposals.filter((p) =>
      liveTaskIds.has(p.task_id as string),
    );
    const acceptedTaskIds = new Set(liveAcceptedProposals.map((p) => p.task_id as string));
    const unstaffed = taskIdRows.filter(
      (t: { id: string; budget_usd: number | null; status: string }) =>
        t.status !== "cancelled" &&
        Number(t.budget_usd ?? 0) > 0 &&
        !acceptedTaskIds.has(t.id),
    );
    if (unstaffed.length > 0) {
      throw new UnstaffedTaskError(unstaffed.map((t: { id: string }) => t.id));
    }
    // A quest that already exists on-chain must be funded on the chain it was
    // created on. One with no chain_quest_id yet has never been on-chain, so
    // it is free to land on whichever chain is active (written at step 8).
    if (questRow.chain_quest_id) assertOnActiveChain(questRow.chain_id, "quest");

    // 1b. Apply accepted proposal side effects (assign workers, update budgets).
    // When the worker accepted the creator's counter-offer, RLS prevented them
    // from updating tasks/quests. The creator does it now.
    // Each proposal's three updates are independent, and proposals don't touch
    // each other's tasks (one accepted proposal per task), so the whole step
    // fans out.
    await Promise.all(
      liveAcceptedProposals.map((proposal) =>
        Promise.all([
          // Assign worker and set negotiated price on the task
          supabase
            .from("tasks")
            .update({
              assignee_id: proposal.worker_id,
              budget_usd: proposal.proposed_price,
              status: "assigned",
            })
            .eq("id", proposal.task_id),
          // Supersede other pending proposals on this task
          supabase
            .from("proposals")
            .update({ status: "superseded", updated_at: new Date().toISOString() })
            .eq("task_id", proposal.task_id)
            .eq("status", "pending")
            .neq("worker_id", proposal.worker_id),
          // Add worker to workspace. ignoreDuplicates → ON CONFLICT DO NOTHING,
          // which avoids the UPDATE branch (workspace_members has no UPDATE policy).
          supabase
            .from("workspace_members")
            .upsert(
              { quest_id: questId, user_id: proposal.worker_id, role: "worker" },
              { onConflict: "quest_id,user_id", ignoreDuplicates: true },
            ),
        ]),
      ),
    );

    // Re-read tasks after applying proposal side effects
    const { data: taskRows, error: tErr } = await supabase
      .from("tasks")
      .select("*")
      .eq("quest_id", questId)
      .order("order", { ascending: true });
    if (tErr) throw new Error(`Failed to fetch tasks: ${tErr.message}`);
    // Supabase can return data=null with error=null on RLS silent block.
    // Guard once so the rest of the flow operates on a known array and
    // every downstream `tasks` becomes a plain `tasks` reference.
    if (!taskRows) {
      throw new Error("Failed to fetch tasks: no data returned (possible RLS block).");
    }
    const tasks = taskRows;

    // A task the creator dropped (Task 3.3) is not funded, not created
    // on-chain, and not counted in the escrow total. Escrow's total is
    // immutable once created, so paying for a cancelled task would strand
    // that money in the vault with no task left to release it.
    const fundableTasks = tasks.filter((t: { status: string }) => t.status !== "cancelled");

    // ── The gate, again, on the rows that actually size the escrow ──────────
    // The check above ran on taskIdRows, read BEFORE step 1b. Everything below
    // — totalBudget, budgetedTasks, the on-chain createTask loop — is built
    // from THIS re-read instead. Nothing keeps the two lists equal: the quest
    // sits in 'negotiating' for the whole call (no 'locking' status is ever
    // written), questEditPermissions leaves task editing open in that status,
    // and tasks_insert_own carries no status restriction at all. So a second
    // tab can add a task between the two reads and have it funded, unstaffed,
    // for real money — the escrow total is immutable once created, so that
    // task can never be funded afterwards and never paid.
    //
    // Re-running the same predicate here is what makes the gate a gate. The
    // first check is still worth keeping: it refuses BEFORE step 1b mutates
    // proposals and workspace_members, which is the failure this ordering was
    // designed around.
    const fundableUnstaffed = fundableTasks.filter(
      (t: { id: string; budget_usd: number | null }) =>
        Number(t.budget_usd ?? 0) > 0 && !acceptedTaskIds.has(t.id as string),
    );
    if (fundableUnstaffed.length > 0) {
      throw new UnstaffedTaskError(fundableUnstaffed.map((t: { id: string }) => t.id as string));
    }

    const rawCurrency = questRow.currency ?? "USDT";
    if (!isKnownCurrency(rawCurrency)) {
      throw new Error(`Unknown currency in quest ${questId}: ${rawCurrency}`);
    }
    const currency: KnownCurrency = rawCurrency;
    const totalBudget = fundableTasks.reduce(
      (sum: number, t: { budget_usd: number }) => sum + Number(t.budget_usd),
      0,
    );

    // ── Volunteer early return: skip on-chain escrow for $0 quests ──
    // The second term cross-checks the quest's own recorded budget so a paid
    // quest cannot renegotiate its way out of escrow. `?? -1` is deliberate:
    // an unreadable budget must NOT look like volunteer work.
    //
    // This check is only meaningful because 20260909160000 forbids dropping
    // quests.total_budget_usd / tasks.budget_usd from >0 to 0 after draft.
    // Without that trigger the comparison is vacuous — acceptProposal already
    // rewrites total_budget_usd to the negotiated task sum before we read it,
    // so both terms would be the same number. Do not remove one without the
    // other. Nothing may write total_budget_usd above this line.
    if (isVolunteerQuest(totalBudget) && isVolunteerQuest(Number(questRow.total_budget_usd ?? -1))) {
      onStep?.("db_save", "active");

      // Update task statuses FIRST — if this fails, quest stays in its current status
      // and the creator can safely retry
      // fundableTasks, not tasks: a cancelled task's status is not "open", so
      // sweeping the unfiltered list UPDATEs a creator-dropped task back to
      // open — re-arming the exact stranding bug 20260917100000 ships to fix.
      for (const task of fundableTasks) {
        const newStatus = task.assignee_id ? "assigned" : "open";
        if (task.status !== newStatus) {
          const { error: taskUpdateErr } = await supabase
            .from("tasks")
            .update({ status: newStatus })
            .eq("id", task.id as string);
          if (taskUpdateErr) throw new Error(`Failed to update task ${task.id} status: ${taskUpdateErr.message}`);
        }
      }

      // Activate quest only after all tasks are in the correct state.
      // total_budget_usd rides along here rather than in a pre-guard write, so
      // the guard above always reads the value as it stood before this call.
      const { error: questUpdateErr } = await supabase
        .from("quests")
        .update({ status: "active", total_budget_usd: totalBudget })
        .eq("id", questId);
      if (questUpdateErr) throw new Error(`Failed to activate volunteer quest: ${questUpdateErr.message}`);

      this.requestQuestImages(questId);

      // Send system message (best-effort, non-critical)
      const { error: msgErr } = await supabase.from("workspace_messages").insert({
        quest_id: questId,
        sender_id: null,
        sender_role: "system",
        content: "🤝 Volunteer quest started! Volunteers can now begin.",
        message_type: "system",
      });
      if (msgErr) console.warn("Failed to send volunteer system message:", msgErr.message);

      onStep?.("db_save", "done");
      return this.getQuest(questId);
    }

    // ── Paid quest: on-chain escrow flow ──
    if (!this.polygonClient) {
      throw new Error("Polygon client not available — cannot perform on-chain operations");
    }

    const cfg = getChainConfig();
    const tokenConfig = getTokenConfigs().get(currency);
    if (!tokenConfig) throw new Error(`Unsupported currency: ${currency}`);

    const deadlineUnix = questRow.deadline
      ? Math.floor(new Date(questRow.deadline as string).getTime() / 1000)
      : Math.floor(Date.now() / 1000) + 365 * 24 * 3600;

    // Testnet preflight: ensure creator has enough tokens
    if (!isMainnetChainId(cfg.chainId)) {
      const userAddr = await this.polygonClient.getAddress();
      const balance = await this.polygonClient.getTokenBalance(currency, userAddr);
      if (balance < totalBudget) {
        await this.polygonClient.mintTestToken(currency, userAddr, totalBudget - balance);
      }
    }

    // Per-tx 429 retries are handled by PolygonClient.executeSend's
    // 4-attempt exponential backoff. Sequential awaits already serialize
    // sponsored sends through the bundler's per-account nonce queue.

    // ── Steps 2-4 are SKIPPED on a resume ───────────────────────────────────
    // A quest that already carries chain_quest_id has already been created and
    // FUNDED on chain. Re-running approve/create/fund would mint a second
    // escrow and orphan the first — EscrowExists does not stop it, because it
    // is keyed to a chain quest id and step 3 would hand it a brand new one.
    // Resuming is the whole point of writing chain_quest_id at 4b below:
    // multi-task makes step 5 an N-transaction sequence, so landing back here
    // after a mid-loop failure is an ordinary outcome, not a corner case.
    let chainQuestId: bigint;
    let chainTxHash: string;

    if (questRow.chain_quest_id) {
      chainQuestId = BigInt(questRow.chain_quest_id as number);
      chainTxHash = (questRow.chain_tx_hash as string | null) ?? "";
      onStep?.("approve", "done");
      onStep?.("create", "done");
      onStep?.("fund", "done");
    } else {
      // 2. Approve tokens
      onStep?.("approve", "active");
      await this.polygonClient.approveToken(currency, cfg.contracts.questManager, totalBudget);
      onStep?.("approve", "done");

      // 3. Create quest on-chain
      onStep?.("create", "active");
      const created = await this.polygonClient.createQuest(
        { title: questRow.title as string, description: questRow.description as string },
        questRow.category as string,
        totalBudget,
        deadlineUnix,
        tokenConfig.address,
      );
      chainQuestId = created.questId;
      chainTxHash = created.receipt.hash;
      onStep?.("create", "done");

      // 4. Fund quest (escrow)
      onStep?.("fund", "active");
      await this.polygonClient.fundQuest(chainQuestId);
      onStep?.("fund", "done");

      // 4b. Record the escrow the INSTANT it exists, before anything else can
      // fail. Money has moved by the line above; until this write lands, a
      // retry cannot tell that and will fund again. Deliberately its own write
      // and deliberately fatal — continuing to create tasks against an escrow
      // we failed to record is exactly how the orphan is made.
      const { error: escrowRecordErr } = await supabase
        .from("quests")
        .update({
          chain_quest_id: Number(chainQuestId),
          chain_tx_hash: chainTxHash,
          chain_id: cfg.chainId,
        })
        .eq("id", questId);
      if (escrowRecordErr) {
        throw new Error(
          `Escrow was funded on-chain (quest ${chainQuestId}) but recording it failed: ` +
            `${escrowRecordErr.message}. Do NOT retry funding — the escrow exists. ` +
            `Record chain_quest_id=${chainQuestId} on quest ${questId} before continuing.`,
        );
      }
    }

    // 5. Create tasks on-chain
    onStep?.("tasks", "active");
    const chainTaskIds: Record<string, number> = {}; // supabase task id -> chain task id
    // Seed from what is already on chain, so a resume's step 7 can still
    // assign workers to tasks an earlier attempt created. Without this the
    // assign loop's `chainTaskIds[...] !== undefined` guard silently skips
    // every task the retry did not itself create.
    for (const task of fundableTasks) {
      if (task.chain_task_id !== null && task.chain_task_id !== undefined) {
        chainTaskIds[task.id as string] = Number(task.chain_task_id);
      }
    }
    // A task that already carries a chain_task_id was created on a previous
    // attempt. Creating it again would mint a duplicate on-chain task against
    // the same escrow and leave the second one unreleasable, so a resume picks
    // up exactly where the failed run stopped.
    const budgetedTasks = fundableTasks.filter(
      (t) => Number(t.budget_usd) > 0 && t.chain_task_id === null,
    );
    for (const task of budgetedTasks) {
      const budget = Number(task.budget_usd);

      const taskDeadline = task.deadline
        ? Math.floor(new Date(task.deadline as string).getTime() / 1000)
        : deadlineUnix;

      const { taskId: chainTaskId } = await this.polygonClient.createTask(
        chainQuestId,
        { title: task.title as string, description: task.description as string },
        task.task_type as "human" | "ai",
        budget,
        Number(task.estimated_effort_hours),
        taskDeadline,
        (task.skills as string[]) ?? [],
        task.order as number,
      );
      chainTaskIds[task.id as string] = Number(chainTaskId);
    }
    onStep?.("tasks", "done");

    // 6. Publish quest + open tasks
    onStep?.("publish", "active");
    await this.polygonClient.publishQuest(BigInt(Number(chainQuestId)));
    await this.polygonClient.openAllTasks(BigInt(Number(chainQuestId)));
    onStep?.("publish", "done");

    // 7. Assign workers on-chain via TaskManager.assignTask (bypasses BountyBoard)
    //
    // Throw loudly if the worker's wallet_address is missing in profiles.
    // Silently skipping the chain call here while later DB writes flip
    // tasks.status → "assigned" produces a divergence where chain
    // task.assignee = 0x0 but DB says assigned, permanently NotAssignee'ing
    // the worker on submitWork. The race window: usePrivySync fires the
    // initial profile insert before useWallets() resolves, so a freshly
    // onboarded worker can have profiles.wallet_address = NULL at the
    // moment a creator accepts their proposal.
    onStep?.("assign", "active");
    // fundableTasks: a cancelled task can still carry an assignee_id, and it
    // has no on-chain task to assign them to. The chainTaskIds guard below
    // already covered that by accident; this makes it the rule.
    for (const task of fundableTasks) {
      const assigneeId = task.assignee_id as string | null;
      const chainTaskId = chainTaskIds[task.id as string];
      if (assigneeId && chainTaskId !== undefined) {
        const { data: wallet } = await supabase.rpc("get_user_wallet", {
          p_user_id: assigneeId,
        });

        if (!wallet) {
          throw new Error(
            `Cannot assign task ${task.id as string}: worker ${assigneeId} ` +
              `has no wallet_address on file. The worker must finish ` +
              `Privy wallet onboarding before the proposal can be accepted.`,
          );
        }

        await this.polygonClient.assignTask(
          BigInt(chainTaskId),
          wallet as string,
        );
      }
    }
    onStep?.("assign", "done");

    // 8. Update Supabase records with chain IDs.
    // Order matters here: the quest update MUST succeed before we land the
    // worker-visible signals (task status flips, "Workers can now start"
    // system message). If the quest write fails after we've already told
    // workers it's go-time, they see a green light against a quest that is
    // still missing chain_quest_id / status="active". So:
    //   step A: quest update (sequential, throws on failure)
    //   step B: task updates + system message (parallel — independent of
    //           each other, all gated on step A succeeding)
    onStep?.("db_save", "active");

    // Quest update first — folds in total_budget_usd so the pre-chain budget
    // write is unnecessary. Throws on failure so steps B never run.
    const { error: questUpdateErr } = await supabase
      .from("quests")
      .update({
        chain_quest_id: Number(chainQuestId),
        chain_tx_hash: chainTxHash,
        chain_id: cfg.chainId,
        status: "active",
        total_budget_usd: totalBudget,
      })
      .eq("id", questId);
    if (questUpdateErr) {
      console.error(
        `[lockFundsAndStart] Chain succeeded but quest DB update failed. ` +
          `questId=${questId}, chainQuestId=${Number(chainQuestId)}, ` +
          `txHash=${chainTxHash}. Manual DB reconciliation needed.`,
        questUpdateErr,
      );
      throw new Error(
        `Failed to save quest chain data (questId=${questId}, chainQuestId=${Number(chainQuestId)}): ${questUpdateErr.message}`,
      );
    }

    this.requestQuestImages(questId);

    // Now safe to fan out the worker-visible signals.
    const tasksWithChainIds = tasks.filter(
      (task) => chainTaskIds[task.id as string] !== undefined,
    );

    // PostgrestFilterBuilder is thenable but not a real Promise — wrap each
    // in Promise.resolve to give Promise.all() a homogeneous array. Tag each
    // write with its identity so a failure throws with the offending task ID
    // (or "system_message") rather than just a generic error string.
    const followupWrites: Array<{
      label: string;
      run: () => Promise<{ error: unknown }>;
    }> = [
      ...tasksWithChainIds.map((task) => ({
        label: `task:${task.id as string}`,
        run: () =>
          Promise.resolve(
            supabase
              .from("tasks")
              .update({
                chain_task_id: chainTaskIds[task.id as string],
                chain_assigned: !!task.assignee_id,
                status: task.assignee_id ? "assigned" : "open",
              })
              .eq("id", task.id as string),
          ) as Promise<{ error: unknown }>,
      })),
      {
        label: "system_message",
        run: () =>
          Promise.resolve(
            supabase.from("workspace_messages").insert({
              quest_id: questId,
              sender_id: null,
              sender_role: "system",
              content: `🔒 Funds locked on-chain ($${totalBudget} ${currency}). Workers can now start.`,
              message_type: "system",
            }),
          ) as Promise<{ error: unknown }>,
      },
    ];

    const writeResults = await Promise.all(
      followupWrites.map(async (w) => ({ label: w.label, result: await w.run() })),
    );
    const failed = writeResults.find((r) => (r.result as { error: unknown }).error != null);
    if (failed) {
      const failureError = (failed.result as { error: { message?: string } }).error;
      console.error(
        `[lockFundsAndStart] Chain succeeded but follow-up DB write failed. ` +
          `questId=${questId}, chainQuestId=${Number(chainQuestId)}, ` +
          `failedWrite=${failed.label}, txHash=${chainTxHash}. Manual reconciliation needed.`,
        failureError,
      );
      throw new Error(
        `Failed to save chain data (failedWrite=${failed.label}): ${failureError?.message ?? "unknown error"}`,
      );
    }

    onStep?.("db_save", "done");

    return this.getQuest(questId);
  }

  async getQuest(id: string): Promise<Quest> {
    // `maybeSingle`, NOT `single`: an absent (or RLS-hidden) quest is an
    // EXPECTED outcome that callers must be able to distinguish, and `single`
    // reports it as PGRST116 — an error indistinguishable from a real transport
    // or permission failure. That conflation is how a cancelled quest and an
    // outage both rendered as "Quest not found.". See lib/questErrors.ts.
    const { data, error } = await supabase
      .from("quests")
      .select("*, tasks(*)")
      .eq("id", id)
      .maybeSingle();

    if (error) throw new Error(`Failed to fetch quest: ${error.message}`);
    if (!data) throw new QuestNotFoundError(id);
    return mapQuestRow(data as QuestRow);
  }

  async listMyQuests(
    userId: string,
    filters?: QuestFilters,
  ): Promise<PaginatedResult<Quest>> {
    const chainId = getChainConfig().chainId;
    let query = supabase
      .from("quests")
      .select("*, tasks(*)", { count: "exact" })
      .eq("creator_id", userId)
      .eq("chain_id", chainId)
      // A stock quest's anchor is a LISTING, not work: it is never funded and
      // never worked, and My Stock Quests already shows it with its slot
      // progress. Left in this list it invited the creator to fund the one
      // quest that must never be funded. Clones stay — each is a real quest
      // with real escrow and a real worker, and hiding those would hide money.
      .eq("is_template_anchor", false)
      .order("created_at", { ascending: false });

    if (filters?.status) {
      query = query.eq("status", filters.status);
    }
    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(`Failed to list quests: ${error.message}`);

    const quests = (data as QuestRow[]).map(mapQuestRow);
    return {
      data: quests,
      total: count ?? quests.length,
      cursor: null,
      hasMore: false,
    };
  }

  /**
   * DEV-824: ask for a re-translation after the source text changed.
   *
   * Fire-and-forget by design. The reset trigger has already blanked `i18n` as
   * part of the UPDATE, so if this never lands the reader sees the creator's
   * own words — never the translation of the text that was just replaced.
   * Failing the edit because a translation could not be scheduled would be
   * strictly worse than the stale-free fallback we already have.
   *
   * DEV-188: also called after a task is INSERTED, which is not a "source text
   * changed" case and is easy to mistake for a redundant call. It is not. A new
   * task on an already-translated quest used to be invisible to every path:
   *
   *   - no reset trigger fires, because nothing was updated;
   *   - the quest row keeps its populated `i18n`, so a sweep keyed only on
   *     `quests WHERE i18n = '{}'` never re-selects the parent;
   *   - `isUpToDate()` in translate-content WOULD catch it (it requires every
   *     task row to be populated), but only if something asks.
   *
   * This is the thing that asks, immediately. It is still best-effort, so
   * `scripts/backfill-quest-translations.ts` now ALSO selects quests that have
   * a task with `i18n = '{}'` — otherwise one failed request here would leave
   * that task untranslated permanently, with nothing able to find it again.
   */
  private requestRetranslation(questId: string): void {
    void this.retranslate(questId);
  }

  /** The request itself, settled rather than thrown — a failure only warns. */
  private retranslate(questId: string): Promise<void> {
    return callEdgeFunction("translate-content", { body: { questId } }).then(
      () => undefined,
      (e) => {
        console.warn("[DEV-824] re-translation request failed", questId, e);
      },
    );
  }

  /**
   * Ask for platform illustrations once a quest becomes publicly visible.
   *
   * Fire-and-forget, and that is the whole safety argument. Every caller below
   * has ALREADY committed — `publishQuest` and `lockFundsAndStart` have settled
   * on-chain by the time this runs, and a chain write cannot be rolled back
   * because an image provider was down. Failing a publish to schedule a
   * decoration would trade an irreversible success for a cosmetic one.
   *
   * Not called on plain edits: images illustrate the work, not its current
   * wording, and re-firing on every save would ask quest-images to re-decide
   * eligibility on every keystroke-sized change. Re-publishing a quest that
   * went back to draft does not re-bill either — the generation claim is a row
   * with the quest id as its PRIMARY KEY, so the second attempt loses in the
   * database rather than here.
   *
   * Dropping a request is survivable precisely because
   * `scripts/backfill-quest-images.ts` can re-find any publicly visible quest
   * with no images and no resolved claim. Without that repair path this kick
   * would be a silent single point of failure, which is the mistake the
   * translation kick had to be patched for after the fact.
   *
   * The create-as-published path is NOT here: that quest is born inside the
   * `quests` edge function and never passes through these methods, so its kick
   * lives there (`buildQuestImagesRequest`).
   */
  private requestQuestImages(questId: string): void {
    // try/catch AND .catch, which is not belt-and-braces. `void p.catch()`
    // only survives a REJECTION; a synchronous throw from callEdgeFunction — a
    // missing session, a bad base URL — escapes it entirely and would fail the
    // caller. On the funds-lock path the caller is a quest whose escrow has
    // already been created and funded, and EscrowVault.createEscrow reverts on
    // a second attempt, so that throw would strand real money to avoid missing
    // a decoration. Promise.resolve() also absorbs a non-promise return.
    try {
      void Promise.resolve(callEdgeFunction("quest-images", { body: { questId } }))
        .catch((e) => {
          console.warn("[quest-images] image generation request failed", questId, e);
        });
    } catch (e) {
      console.warn("[quest-images] image generation request failed", questId, e);
    }
  }

  async updateQuest(
    id: string,
    data: Partial<Quest>,
    opts?: UpdateQuestOptions,
  ): Promise<Quest> {
    if (opts?.asAdmin) return this.adminUpdateQuest(id, data);

    const updates: Record<string, unknown> = {};
    if (data.title !== undefined) updates.title = data.title;
    if (data.description !== undefined) updates.description = data.description;
    if (data.category !== undefined) updates.category = data.category;
    if (data.totalBudgetUSD !== undefined)
      updates.total_budget_usd = data.totalBudgetUSD;
    if (data.deadline !== undefined) updates.deadline = data.deadline;

    const { data: row, error } = await supabase
      .from("quests")
      .update(updates)
      .eq("id", id)
      .select("*, tasks(*)")
      .single();

    if (error) throw new Error(`Failed to update quest: ${error.message}`);

    if (data.title !== undefined || data.description !== undefined) {
      this.requestRetranslation(id);
      this.requestListingRetranslation(row as QuestRow);
    }

    return mapQuestRow(row as QuestRow);
  }

  /**
   * A stock listing's anchor edit is carried to every live instance by the
   * `quests_sync_stock_listing_content` trigger (20260927120000), and each of
   * those rows has its `i18n` blanked in the same statement. Nothing else
   * would ask for them to be re-translated short of a manual backfill run, so ask
   * per instance here. Instances already reading different text were left
   * alone by the trigger and are simply up to date to translate-content.
   *
   * Fire-and-forget for the same reason as `requestRetranslation`: the edit
   * has committed, and a failed lookup leaves readers the creator's own words.
   * Serial, not parallel: a full listing has up to 100 instances, and
   * translate-content sits on a rate-limited LLM upstream — the same reason
   * scripts/backfill-quest-translations.ts runs one quest at a time.
   */
  private requestListingRetranslation(row: Pick<QuestRow, "id" | "template_id" | "is_template_anchor">): void {
    if (!row.is_template_anchor || !row.template_id) return;
    void (async () => {
      const { data, error } = await supabase
        .from("quests")
        .select("id")
        .eq("template_id", row.template_id)
        .neq("id", row.id)
        .not("status", "in", `(${TERMINAL_QUEST_STATUSES.join(",")})`);
      if (error) {
        console.warn("[stock-listing] instance lookup for re-translation failed", row.id, error);
        return;
      }
      for (const instance of (data ?? []) as { id: string }[]) {
        await this.retranslate(instance.id);
      }
    })().catch((e) => {
      console.warn("[stock-listing] instance re-translation failed", row.id, e);
    });
  }

  /**
   * The admin write path (DEV-792). Not an alternative spelling of the owner
   * update — a different, narrower door to the same four columns:
   *
   *  - `admin_update_quest` is SECURITY DEFINER behind `is_admin()`, so the
   *    authority is the database, not this branch. Admins hold SELECT-only
   *    policies on `quests`; a table update here would simply be refused.
   *  - The RPC's five parameters are the column allowlist. `creator_id`,
   *    `status`, `chain_id`, `total_budget_usd` and `details_verified_*` are
   *    unreachable through it by construction.
   *  - It has no parameter defaults, so every parameter is sent on every call.
   *    An omitted argument would break signature resolution and surface as an
   *    opaque PostgREST error; a null one reaches the RPC's own named guard
   *    ("Quest title cannot be empty"). Hence `?? null`, and hence this being
   *    a whole-details write rather than a patch.
   *  - RETURNS VOID, so the row is re-read to keep `updateQuest`'s contract.
   */
  private async adminUpdateQuest(id: string, data: Partial<Quest>): Promise<Quest> {
    const { error } = await supabase.rpc("admin_update_quest", {
      p_id: id,
      p_title: data.title ?? null,
      p_description: data.description ?? null,
      p_category: data.category ?? null,
      p_deadline: data.deadline ?? null,
    });

    if (error) throw new Error(`Failed to update quest: ${error.message}`);

    // Past this point the write has COMMITTED. Everything below is read-back,
    // so it must not be ordered as though it were part of the write.
    //
    // Same trigger as the owner path: an admin correcting a description must
    // not leave the es/pt copies describing the old text (DEV-824). Scheduled
    // here rather than after the re-read, because the text has already changed
    // — a re-read that fails must not also cost us the retranslation.
    if (data.title !== undefined || data.description !== undefined) {
      this.requestRetranslation(id);
    }

    const { data: row, error: readError } = await supabase
      .from("quests")
      .select("*, tasks(*)")
      .eq("id", id)
      .single();

    // Distinct message from the write failure above. The RPC RETURNS VOID, so
    // this second round trip only exists to honour updateQuest's `Promise<Quest>`
    // contract; if it fails the edit is already saved, and telling the operator
    // "failed to update" would send them to re-enter data they have not lost.
    // `useUpdateQuest` invalidates on settle, so the page still refetches here.
    if (readError) {
      throw new Error(`Quest updated, but reloading it failed: ${readError.message}`);
    }

    // After the re-read, not beside requestRetranslation above: whether this is
    // a listing is only known from the row. If the re-read failed, the
    // backfill script (scripts/backfill-quest-translations.ts) still finds
    // the instances — their i18n is '{}'.
    if (data.title !== undefined || data.description !== undefined) {
      this.requestListingRetranslation(row as QuestRow);
    }

    return mapQuestRow(row as QuestRow);
  }

  async deleteQuest(id: string): Promise<void> {
    const { error } = await supabase.from("quests").delete().eq("id", id);
    if (error) throw new Error(`Failed to delete quest: ${error.message}`);
  }

  async addTask(questId: string, task: CreateTaskInput): Promise<Task> {
    // If quest has a chain ID, also create task on-chain
    if (this.polygonClient) {
      const { data: quest } = await supabase
        .from("quests")
        .select("chain_quest_id, deadline, chain_id")
        .eq("id", questId)
        .single();

      if (quest?.chain_quest_id) {
        assertOnActiveChain(quest.chain_id, "quest");
        const deadlineUnix = task.deadline
          ? Math.floor(new Date(task.deadline).getTime() / 1000)
          : quest.deadline
            ? Math.floor(new Date(quest.deadline).getTime() / 1000)
            : Math.floor(Date.now() / 1000) + 365 * 24 * 3600;

        const { taskId } = await this.polygonClient.createTask(
          BigInt(quest.chain_quest_id),
          { title: task.title, description: task.description },
          task.taskType,
          task.budgetUSD ?? 0,
          task.estimatedEffortHours,
          deadlineUnix,
          task.skills,
          task.order ?? 1,
        );

        const { data, error } = await supabase
          .from("tasks")
          .insert({
            quest_id: questId,
            title: task.title,
            description: task.description,
            task_type: task.taskType,
            budget_usd: task.budgetUSD ?? 0,
            estimated_effort_hours: task.estimatedEffortHours,
            skills: task.skills,
            deadline: task.deadline ?? null,
            order: task.order ?? 0,
            status: "open",
            chain_task_id: Number(taskId),
            work_mode: task.workMode ?? "remote",
            location_countries: task.locationCountries ?? [],
            location_cities: task.locationCities ?? [],
          })
          .select()
          .single();

        if (error) throw new Error(`Failed to add task: ${error.message}`);
        this.requestRetranslation(questId);
        return mapTaskRow(data as TaskRow);
      }
    }

    // Supabase-only fallback
    const { data, error } = await supabase
      .from("tasks")
      .insert({
        quest_id: questId,
        title: task.title,
        description: task.description,
        task_type: task.taskType,
        budget_usd: task.budgetUSD ?? 0,
        estimated_effort_hours: task.estimatedEffortHours,
        skills: task.skills,
        deadline: task.deadline ?? null,
        order: task.order ?? 0,
        status: "open",
        work_mode: task.workMode ?? "remote",
        location_countries: task.locationCountries ?? [],
        location_cities: task.locationCities ?? [],
      })
      .select()
      .single();

    if (error) throw new Error(`Failed to add task: ${error.message}`);
    this.requestRetranslation(questId);
    return mapTaskRow(data as TaskRow);
  }

  async updateTask(taskId: string, data: Partial<Task>): Promise<Task> {
    const updates: Record<string, unknown> = {};
    if (data.title !== undefined) updates.title = data.title;
    if (data.description !== undefined) updates.description = data.description;
    if (data.taskType !== undefined) updates.task_type = data.taskType;
    if (data.budgetUSD !== undefined) updates.budget_usd = data.budgetUSD;
    if (data.estimatedEffortHours !== undefined)
      updates.estimated_effort_hours = data.estimatedEffortHours;
    if (data.skills !== undefined) updates.skills = data.skills;
    if (data.deadline !== undefined) updates.deadline = data.deadline;
    if (data.status !== undefined) updates.status = data.status;
    if (data.workMode !== undefined) updates.work_mode = data.workMode;
    if (data.locationCountries !== undefined) updates.location_countries = data.locationCountries;
    if (data.locationCities !== undefined) updates.location_cities = data.locationCities;
    if (data.trustFactors !== undefined) {
      updates.trust_factors = sanitizeTrustFactors(data.trustFactors);
      updates.trust_factors_source = "creator_edited";
    }

    const { data: row, error } = await supabase
      .from("tasks")
      .update(updates)
      .eq("id", taskId)
      .select()
      .single();

    if (error) throw new Error(`Failed to update task: ${error.message}`);

    if (data.title !== undefined || data.description !== undefined) {
      this.requestRetranslation((row as TaskRow).quest_id);
    }

    return mapTaskRow(row as TaskRow);
  }

  async adminUpdateQuestLocation(
    questId: string,
    workMode: string,
    locationCountries: string[],
    locationCities: string[],
  ): Promise<void> {
    const { error } = await supabase.rpc("admin_update_quest_location", {
      p_quest_id: questId,
      p_work_mode: workMode,
      p_location_countries: locationCountries,
      p_location_cities: locationCities,
    });
    if (error) throw new Error(`Failed to update quest location: ${error.message}`);
  }

  async deleteTask(taskId: string): Promise<void> {
    const { error } = await supabase.from("tasks").delete().eq("id", taskId);
    if (error) throw new Error(`Failed to delete task: ${error.message}`);
  }

  async addSubtask(
    parentTaskId: string,
    subtask: CreateTaskInput,
  ): Promise<Task> {
    const { data: parent, error: parentError } = await supabase
      .from("tasks")
      .select("quest_id")
      .eq("id", parentTaskId)
      .single();

    if (parentError)
      throw new Error(`Failed to find parent task: ${parentError.message}`);

    const { data, error } = await supabase
      .from("tasks")
      .insert({
        quest_id: parent.quest_id,
        parent_task_id: parentTaskId,
        title: subtask.title,
        description: subtask.description,
        task_type: subtask.taskType,
        budget_usd: subtask.budgetUSD ?? 0,
        estimated_effort_hours: subtask.estimatedEffortHours,
        skills: subtask.skills,
        deadline: subtask.deadline ?? null,
        order: subtask.order ?? 0,
        status: "open",
        work_mode: subtask.workMode ?? "remote",
        location_countries: subtask.locationCountries ?? [],
        location_cities: subtask.locationCities ?? [],
      })
      .select()
      .single();

    if (error) throw new Error(`Failed to add subtask: ${error.message}`);
    this.requestRetranslation(parent.quest_id as string);
    return mapTaskRow(data as TaskRow);
  }

  async getTaskTree(questId: string): Promise<TaskTreeNode[]> {
    const { data, error } = await supabase
      .from("tasks")
      .select("*")
      .eq("quest_id", questId)
      .order("order", { ascending: true });

    if (error) throw new Error(`Failed to fetch task tree: ${error.message}`);
    const tasks = (data as TaskRow[]).map(mapTaskRow);
    return buildTaskTree(tasks);
  }

  async getTaskQuestId(taskId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from("tasks")
      .select("quest_id")
      .eq("id", taskId)
      .maybeSingle();

    if (error) throw new Error(`Failed to fetch task: ${error.message}`);
    return (data?.quest_id as string | undefined) ?? null;
  }

  async publishQuest(id: string): Promise<Quest> {
    // Chain-first: publish on-chain
    if (this.polygonClient) {
      const { data: quest } = await supabase
        .from("quests")
        .select("chain_quest_id, chain_id")
        .eq("id", id)
        .single();

      if (quest?.chain_quest_id) {
        assertOnActiveChain(quest.chain_id, "quest");
        await this.polygonClient.publishQuest(BigInt(quest.chain_quest_id));
      }
    }

    // DB-second: update Supabase
    const { data, error } = await supabase
      .from("quests")
      .update({ status: "active" })
      .eq("id", id)
      .select("*, tasks(*)")
      .single();

    if (error) throw new Error(`Failed to publish quest: ${error.message}`);

    this.requestQuestImages(id);
    return mapQuestRow(data as QuestRow);
  }

  async closeQuest(id: string): Promise<Quest> {
    // Chain-first: close on-chain (settles escrow, refunds remaining)
    if (this.polygonClient) {
      const { data: quest } = await supabase
        .from("quests")
        .select("chain_quest_id, chain_id")
        .eq("id", id)
        .single();

      if (quest?.chain_quest_id) {
        assertOnActiveChain(quest.chain_id, "quest");
        await this.polygonClient.closeQuest(BigInt(quest.chain_quest_id));
      }
    }

    // DB-second: update Supabase
    const { data, error } = await supabase
      .from("quests")
      .update({ status: "closed" })
      .eq("id", id)
      .select("*, tasks(*)")
      .single();

    if (error) throw new Error(`Failed to close quest: ${error.message}`);
    return mapQuestRow(data as QuestRow);
  }

  async cancelQuest(id: string): Promise<Quest> {
    const CANCELLABLE_STATUSES = ["draft", "published", "negotiating", "locking", "active"];

    // Read current quest to validate status and get chain ID
    const { data: quest, error: readErr } = await supabase
      .from("quests")
      .select("status, chain_quest_id, chain_id, template_id, is_template_anchor")
      .eq("id", id)
      .single();

    if (readErr) throw new Error(`Failed to read quest for cancellation: ${readErr.message}`);
    if (!CANCELLABLE_STATUSES.includes(quest.status)) {
      throw new Error(`Quest cannot be cancelled in "${quest.status}" status`);
    }

    // Chain-first: cancel on-chain (refunds locked funds to creator).
    // cancelQuest refunds real money, and chain_quest_id only identifies a
    // quest within its own chain -- so never let a switched session cancel
    // whatever happens to hold that id on the other network.
    if (quest.chain_quest_id) {
      assertOnActiveChain(quest.chain_id, "quest");
      if (!this.polygonClient) {
        throw new Error("Wallet connection required to cancel an on-chain quest");
      }
      await this.polygonClient.cancelQuest(BigInt(quest.chain_quest_id));
    }

    // DB-second: update Supabase (with status guard to prevent races).
    // maybeSingle, not single: the on-chain refund above has ALREADY happened,
    // so a zero-row result is not an error to report as a failed cancellation.
    // It means someone else (an admin, via admin_cancel_quest) moved the status
    // out of CANCELLABLE_STATUSES while we were on-chain. Telling the creator
    // "failed" there would invite a retry of a refund that already succeeded.
    const { data, error } = await supabase
      .from("quests")
      .update({ status: "cancelled" })
      .eq("id", id)
      .in("status", CANCELLABLE_STATUSES)
      .select("*, tasks(*)")
      .maybeSingle();

    if (error) throw new Error(`Failed to cancel quest: ${error.message}`);

    // Released here, above the lost-race branch below, rather than after it.
    // release_template_slot re-validates status = 'cancelled' under FOR UPDATE
    // and is idempotent, so running it on the path where someone else wrote the
    // status is both safe and necessary: that path used to return early and
    // burn the slot for good.
    await this.releaseTemplateSlot(id, quest);

    if (!data) {
      const { data: current } = await supabase
        .from("quests")
        .select("*, tasks(*)")
        .eq("id", id)
        .maybeSingle();

      if (current) {
        // The refund landed; only the status write lost the race.
        return mapQuestRow(current as QuestRow);
      }
      throw new Error(
        "Quest was cancelled on-chain and the refund completed, but its status " +
          "could not be read back afterwards. Do not retry — reload the quest.",
      );
    }

    return mapQuestRow(data as QuestRow);
  }

  /**
   * DEV-318: a cancelled clone gives its slot back to the stock quest it came
   * from — but only while no money was ever on it. `chain_quest_id` is the
   * test: once escrow exists the slot is spent, or a creator could churn
   * workers through a 3-slot listing indefinitely.
   *
   * Called after the cancellation has landed, and deliberately does NOT throw.
   * By this point the on-chain refund and the status write are both done, so
   * reporting a failed cancellation would invite a retry of a refund that
   * already succeeded. The RPC is idempotent and safe to re-run, so a loud log
   * is the honest outcome: the cancellation stands, the slot is recoverable.
   */
  private async releaseTemplateSlot(
    questId: string,
    quest: { template_id?: string | null; is_template_anchor?: boolean | null; chain_quest_id?: number | null },
  ): Promise<void> {
    if (!quest.template_id || quest.is_template_anchor || quest.chain_quest_id) return;

    const { error } = await supabase.rpc("release_template_slot", {
      p_quest_id: questId,
    });

    if (error) {
      const message =
        `[SupabaseQuestService] Quest ${questId} was cancelled but its stock-quest ` +
        `slot was not released: ${error.message}. The listing will show one ` +
        `fewer opening than it has. Re-running release_template_slot is safe.`;
      console.error(message);
      // Capacity drifting away one silent failure at a time is exactly the bug
      // 20260917130000 was written to end, so this cannot stay console-only:
      // nobody reads a user's console. Reported, not thrown -- the refund and
      // the cancellation have both already landed.
      Sentry.captureException(error instanceof Error ? error : new Error(message), {
        tags: { feature: "quest-stock", operation: "release_template_slot" },
        extra: { questId, templateId: quest.template_id },
      });
    }
  }

}

// ─── Helpers ─────────────────────────────────────────────

interface TaskPayload {
  title: string;
  description: string;
  taskType: string;
  estimatedEffortHours: number;
  budgetUSD: number;
  skills: string[];
  order: number;
  trustFactors?: TrustFactors;
  trustFactorsSource?: TrustFactorsSource;
  children?: TaskPayload[];
}

function mapCreateTaskInputs(tasks: CreateTaskInput[]): TaskPayload[] {
  return tasks.map((t) => ({
    title: t.title,
    description: t.description,
    taskType: t.taskType,
    estimatedEffortHours: t.estimatedEffortHours,
    budgetUSD: t.budgetUSD ?? 0,
    skills: t.skills,
    order: t.order ?? 0,
    trustFactors: t.trustFactors,
    trustFactorsSource: t.trustFactorsSource,
    children: t.subtasks?.length
      ? mapCreateTaskInputs(t.subtasks)
      : undefined,
  }));
}

/** Flatten nested task tree into a flat array for on-chain creation */
function flattenTasks(tasks: CreateTaskInput[]): CreateTaskInput[] {
  const result: CreateTaskInput[] = [];
  for (const t of tasks) {
    result.push(t);
    if (t.subtasks?.length) {
      result.push(...flattenTasks(t.subtasks));
    }
  }
  return result;
}
